import { addDays } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { matchPath, useLocation, useNavigate, useRevalidator } from "react-router-dom";

import { useDebounce } from "../../../../../hooks/useDebounce";
import { useDebouncedEffect } from "../../../../../hooks/useDebounceEffect";
import useService from "../../../../../hooks/useService";
import { IndexedDBService } from "../../../../../services/indexed-db-service";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import {
  extractActingUser,
  extractCase,
  extractDetail,
  extractLawFirmInfo,
  extractProvider,
} from "../utils/extractors";
import {
  normalizeAssignableUserOption,
  normalizeCaseOption,
  normalizeLawFirmOption,
  normalizeRoleOption,
} from "../utils/normalizers";

const buildQueryString = (params = {}) => {
  const entries = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null && value !== ""
  );
  if (!entries.length) {
    return "";
  }
  const query = entries.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join("&");
  return `?${query}`;
};

export const useAddActionItemModal = ({
  addOpen,
  setAddOpen,
  activeActionItem,
  setActiveActionItem,
  onSuccess = () => {},
  caseObj,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { revalidate } = useRevalidator();

  const indexedDBService = useMemo(() => new IndexedDBService("ActionItems", "details"), []);

  const caseRepository = useService("repositories/case-repository");
  const enumRepository = useService("repositories/enum-repository");
  const actionableItemsRepository = useService("repositories/actionable-items-repository");

  const defaultCaseOption = useMemo(() => normalizeCaseOption(caseObj), [caseObj]);

  const [initialCaseId, setInitialCaseId] = useState(null);
  const [dateTimeVal, setDateTimeValue] = useState();
  const [openError, setOpenError] = useState(false);
  const [errorArray, setErrorArray] = useState([]);

  const [selectedCase, setSelectedCase] = useState(defaultCaseOption);
  const [selectedActingUser, setSelectedActingUser] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [assignableUserList, setAssignableUserList] = useState([]);
  const [roleList, setRoleList] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);

  const [lawFirm, setLawFirm] = useState(false);
  const [lawFirmList, setLawFirmList] = useState([]);
  const [selectedLawFirm, setSelectedLawFirm] = useState(null);
  const [clientCases, setClientCases] = useState([]);

  const [caseSearch, setCaseSearch] = useState("");
  const [actionDetail, setActionDetail] = useState(null);
  const [draftDetail, setDraftDetail] = useState(null);
  const [toggleAddProvider, setToggleAddProvider] = useState(false);
  const [providerList, setProviderList] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [lawFirmSearch, setLawFirmSearch] = useState("");
  const [providerSearch, setProviderSearch] = useState("");
  const [isLoadingActionItem, setIsLoadingActionItem] = useState(false);
  const hydrationActiveRef = useRef(false);
  const draftHydrationActiveRef = useRef(false);
  const prevAddOpenRef = useRef(false);

  const debouncedDetail = useDebounce(actionDetail, 500);
  const baseIndexedKey = useMemo(
    () => selectedCase?.id ?? selectedCase?.pk ?? location.pathname,
    [selectedCase, location.pathname]
  );
  const indexedDBKey = `${baseIndexedKey ?? ""}${
    activeActionItem ? `_${activeActionItem?.id}` : ""
  }`;

  const defaultDate = useMemo(() => addDays(new Date(), 7), []);

  const resetFormState = useCallback(
    (preserveCase = false) => {
      setSelectedActingUser(null);
      setSelectedRole(null);
      setSelectedProvider(null);
      setToggleAddProvider(false);
      setLawFirm(false);
      setSelectedLawFirm(null);
      setActionDetail(null);
      setDraftDetail(null);
      setDateTimeValue(null);
      setIsLoadingActionItem(false);

      if (preserveCase && defaultCaseOption) {
        setSelectedCase(defaultCaseOption);
        setClientCases((prev) => {
          if (prev.some((option) => option?.id === defaultCaseOption.id)) {
            return prev;
          }
          return [defaultCaseOption, ...prev];
        });
      } else if (!preserveCase) {
        setSelectedCase(null);
        setClientCases([]);
      }
    },
    [defaultCaseOption]
  );

  const currentCaseId = selectedCase?.id ?? selectedCase?.pk ?? null;
  const activeActionItemId = activeActionItem?.id ?? null;

  const match = matchPath("/client-detail/:caseId/actionable-items", location.pathname);

  const isOnSameCaseActionables =
    !!match && currentCaseId && String(match.params.caseId) === String(currentCaseId);

  const caseChanged = !!(
    initialCaseId &&
    currentCaseId &&
    String(initialCaseId) !== String(currentCaseId)
  );

  const linkDisabled = !currentCaseId || isOnSameCaseActionables || caseChanged;

  const tooltipTitle = !currentCaseId
    ? "Select a case first"
    : caseChanged
      ? "Case changed — link disabled"
      : isOnSameCaseActionables
        ? "You're already on this case"
        : "View client actionable items";

  useEffect(() => {
    if (addOpen && currentCaseId && initialCaseId === null) {
      setInitialCaseId(String(currentCaseId));
    }
    if (!addOpen) {
      setInitialCaseId(null);
    }
  }, [addOpen, currentCaseId, initialCaseId]);

  useEffect(() => {
    if (activeActionItem) {
      return;
    }

    if (!defaultCaseOption) {
      setSelectedCase(null);
      return;
    }

    setSelectedCase((prev) => {
      if (prev && prev.id === defaultCaseOption.id) {
        return prev;
      }
      return defaultCaseOption;
    });
  }, [activeActionItem, defaultCaseOption]);

  useEffect(() => {
    if (activeActionItem || !defaultCaseOption) {
      return;
    }

    setClientCases((prev) => {
      if (prev.some((option) => option?.id === defaultCaseOption.id)) {
        return prev;
      }

      return [defaultCaseOption, ...prev];
    });
  }, [activeActionItem, defaultCaseOption]);

  useEffect(() => {
    const wasOpen = prevAddOpenRef.current;
    if (addOpen !== wasOpen) {
      prevAddOpenRef.current = addOpen;
    }

    if (!(addOpen && !wasOpen) || activeActionItem) {
      return;
    }

    setDraftDetail(null);
    setActionDetail(null);
    setDateTimeValue(null);
    setSelectedActingUser(null);
    setSelectedRole(null);
    setSelectedProvider(null);
    setToggleAddProvider(false);
    setProviderSearch("");
    setSelectedLawFirm(null);
    setLawFirm(false);
    setLawFirmSearch("");

    setAssignableUserList([]);
    setRoleList([]);
    setProviderList([]);

    if (baseIndexedKey) {
      indexedDBService.delete(baseIndexedKey);
    }
  }, [addOpen, activeActionItem, baseIndexedKey, indexedDBService]);

  useEffect(() => {
    if (!activeActionItem) {
      return;
    }

    draftHydrationActiveRef.current = true;
    indexedDBService.get(indexedDBKey).then((detail) => {
      if (!draftHydrationActiveRef.current || !detail) {
        return;
      }
      setDraftDetail(detail);
    });

    return () => {
      draftHydrationActiveRef.current = false;
    };
  }, [indexedDBKey, activeActionItem, indexedDBService]);

  useEffect(() => {
    if (debouncedDetail !== undefined) {
      indexedDBService.save(indexedDBKey, debouncedDetail);
    }
  }, [debouncedDetail, indexedDBKey, indexedDBService]);

  useEffect(() => {
    hydrationActiveRef.current = true;

    const hydrateFromActionItem = (item) => {
      if (!hydrationActiveRef.current || !item) {
        return;
      }

      setSelectedCase(normalizeCaseOption(extractCase(item)));
      setSelectedActingUser(normalizeAssignableUserOption(extractActingUser(item)));
      setSelectedRole(normalizeRoleOption(item?.assignedRole || item?.assigned_role));
      setActionDetail(extractDetail(item));

      const dueDateValue = item?.dueDate ?? item?.due_date ?? null;
      setDateTimeValue(dueDateValue ? new Date(dueDateValue) : undefined);

      const providerOption = extractProvider(item);
      setSelectedProvider(providerOption ?? null);
      setToggleAddProvider(
        !!(
          providerOption?.id ||
          item?.onProvider?.id ||
          item?.onProviderId?.id ||
          item?.on_provider?.id ||
          item?.on_provider?.pk ||
          item?.onProviderId
        )
      );

      const { hasLawFirmAccess, normalizedLawFirm } = extractLawFirmInfo(item);
      setLawFirm(hasLawFirmAccess);
      setSelectedLawFirm(normalizedLawFirm);
    };

    if (!addOpen) {
      setIsLoadingActionItem(false);
      return () => {
        hydrationActiveRef.current = false;
      };
    }

    if (!activeActionItemId) {
      resetFormState(true);
      return () => {
        hydrationActiveRef.current = false;
      };
    }

    resetFormState(false);
    setIsLoadingActionItem(true);

    actionableItemsRepository
      .findRecord(activeActionItemId, { noCache: true })
      .then((item) => {
        hydrateFromActionItem(item);
        const normalizedCase = normalizeCaseOption(extractCase(item));
        if (normalizedCase) {
          setClientCases((prev) => {
            if (prev.some((option) => option?.id === normalizedCase.id)) {
              return prev;
            }
            return [normalizedCase, ...prev];
          });
        }
      })
      .catch((error) => {
        if (!hydrationActiveRef.current) {
          return;
        }
        const err = ApiRequestErrorHandler(error);
        setErrorArray(err);
        setOpenError(true);
      })
      .finally(() => {
        if (!hydrationActiveRef.current) {
          return;
        }
        setIsLoadingActionItem(false);
      });

    return () => {
      hydrationActiveRef.current = false;
    };
  }, [addOpen, activeActionItemId, actionableItemsRepository, resetFormState]);

  const getUsers = async () => {
    try {
      const urlPath = `core/dynamic-options/assignable-users/${buildQueryString({ search: userSearch })}`;
      const results = await enumRepository.query({ urlPath });

      const normalized = (results ?? []).map((item) => normalizeAssignableUserOption(item));
      setAssignableUserList(normalized);
    } catch (error) {
      const err = ApiRequestErrorHandler(error);
      setErrorArray(err);
      setOpenError(true);
    }
  };

  const getRoles = async () => {
    try {
      const urlPath = `core/dynamic-options/user-roles/${buildQueryString({ search: roleSearch })}`;
      const results = await enumRepository.query({ urlPath });

      const normalized = (results ?? []).map((item) => normalizeRoleOption(item));
      setRoleList(normalized);
    } catch (error) {
      const err = ApiRequestErrorHandler(error);
      setErrorArray(err);
      setOpenError(true);
    }
  };

  const getProviders = async () => {
    if (!selectedCase) {
      setProviderList([]);
      return;
    }

    try {
      const caseQuery = selectedCase?.pk ?? selectedCase?.id;
      const urlPath = `core/dynamic-options/providers/${buildQueryString({
        case: caseQuery,
        search: providerSearch,
      })}`;
      const results = await enumRepository.query({ urlPath });

      const normalized = (results ?? []).map((item) => {
        const id = item?.id ?? item?.pk ?? item?.key;
        const label = item?.label ?? item?.value ?? "";
        return {
          ...item,
          id,
          pk: item?.pk ?? id,
          label,
          value: item?.value ?? label,
        };
      });

      setProviderList(normalized);
    } catch (error) {
      const err = ApiRequestErrorHandler(error);
      setErrorArray(err);
      setOpenError(true);
    }
  };

  const getLawFirms = async () => {
    try {
      const urlPath = `core/dynamic-options/law-firms/${buildQueryString({ search: lawFirmSearch })}`;
      const results = await enumRepository.query({ urlPath });

      const normalized = (results ?? []).map((item) => normalizeLawFirmOption(item));
      setLawFirmList(normalized);
    } catch (error) {
      const err = ApiRequestErrorHandler(error);
      setErrorArray(err);
      setOpenError(true);
    }
  };

  useDebouncedEffect(() => getUsers(), [selectedCase, userSearch], 250);

  useDebouncedEffect(() => getProviders(), [selectedCase, providerSearch], 250);

  useDebouncedEffect(
    () => {
      if (!addOpen) {
        return;
      }
      getLawFirms();
    },
    [lawFirmSearch, addOpen],
    250
  );

  useDebouncedEffect(
    () => {
      if (!addOpen) {
        return;
      }
      getRoles();
    },
    [roleSearch, addOpen],
    250
  );

  useDebouncedEffect(
    async () => {
      if (!addOpen) {
        return;
      }
      try {
        const urlPath = `core/dynamic-options/cases/${buildQueryString({ search: caseSearch })}`;
        const results = await enumRepository.query({ urlPath });

        const normalized = (results ?? []).map((item) => normalizeCaseOption(item));
        const includeDefaultCase = !activeActionItem && defaultCaseOption;

        const combined = includeDefaultCase
          ? [
              defaultCaseOption,
              ...normalized.filter((option) => option?.id !== defaultCaseOption.id),
            ]
          : normalized;

        setClientCases(combined);
      } catch (error) {
        const err = ApiRequestErrorHandler(error);
        setErrorArray(err);
        setOpenError(true);
      }
    },
    [caseSearch, addOpen, activeActionItem, defaultCaseOption],
    250
  );

  const ensureLawFirmId = async () => {
    if (!lawFirm) {
      return null;
    }

    const currentCase = await caseRepository.findRecord(selectedCase?.id);
    return currentCase?.lawFirm ?? null;
  };

  const handleNewActionSubmit = async ({ externalRevalidate }) => {
    if (!selectedActingUser && !selectedRole) {
      setOpenError(true);
      setErrorArray(["You must select either a Role or a User before proceeding"]);
      return;
    }

    const lawFirmId = await ensureLawFirmId();
    const actingUserId = selectedActingUser?.id;
    const assignedRoleId = selectedRole?.id;

    const postData = {
      on_case: selectedCase?.id,
      ...(actingUserId ? { acting_user: actingUserId } : {}),
      action_detail: actionDetail,
      ...(assignedRoleId ? { assigned_role: assignedRoleId } : {}),
      ...(!toggleAddProvider || !selectedProvider ? {} : { on_provider: selectedProvider?.id }),
      on_firm: lawFirm && selectedLawFirm ? selectedLawFirm?.id : lawFirm ? lawFirmId : null,
      due_date: !dateTimeVal ? defaultDate : dateTimeVal,
      status: "pending",
    };

    try {
      await actionableItemsRepository.createRecord(postData);
      setActionDetail(undefined);
      await indexedDBService.delete(indexedDBKey);
      await (externalRevalidate?.() ?? revalidate());
      onSuccess();
      setAddOpen(false);
    } catch (error) {
      if (error?.response) {
        const res = error.response.data;
        const errArr = ApiRequestErrorHandler(res);
        setErrorArray(errArr);
        setOpenError(true);
      }
    }
  };

  const patchAction = async ({ externalRevalidate, markDone = false }) => {
    if (!selectedActingUser && !selectedRole) {
      setOpenError(true);
      setErrorArray(["You must select either a Role or a User before proceeding"]);
      return;
    }

    const lawFirmId = await ensureLawFirmId();
    const actingUserId = selectedActingUser?.id ?? null;
    const assignedRoleId = selectedRole?.id ?? null;

    const patchData = {
      on_case: selectedCase?.id,
      acting_user: actingUserId,
      action_detail: actionDetail,
      due_date: !dateTimeVal ? new Date() : dateTimeVal,
      on_provider: toggleAddProvider && selectedProvider ? selectedProvider?.id : null,
      on_firm: lawFirm && selectedLawFirm ? selectedLawFirm?.id : lawFirm ? lawFirmId : null,
      assigned_role: assignedRoleId,
      ...(markDone ? { status: "done" } : {}),
    };

    try {
      await actionableItemsRepository.patch(activeActionItem?.id, patchData);
      setActionDetail(undefined);
      await indexedDBService.delete(indexedDBKey);
      await (externalRevalidate?.() ?? revalidate());
      onSuccess();
      setAddOpen(false);
    } catch (error) {
      if (error?.response) {
        const errArr = ApiRequestErrorHandler(error);
        setErrorArray(errArr);
        setOpenError(true);
      }
    }
  };

  const handleClose = () => {
    setAddOpen(false);
    setSelectedActingUser(null);
    setSelectedCase(null);
    setActionDetail("");
    setDateTimeValue("");
    setLawFirm(false);
    setSelectedLawFirm(null);
    setSelectedRole(null);
    setRoleSearch("");
    setProviderSearch("");
    setLawFirmSearch("");
    if (activeActionItem) {
      setActiveActionItem(null);
    }
  };

  const handleProvCheck = () => {
    setToggleAddProvider((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedProvider(null);
        setProviderSearch("");
      }
      return next;
    });
  };

  const handleActingUserSelect = (event, user) => {
    const normalizedUser = normalizeAssignableUserOption(user);
    setSelectedActingUser(normalizedUser);
    if (normalizedUser) {
      setSelectedRole(null);
    }
  };

  const handleRoleSelect = (event, role) => {
    const normalizedRole = normalizeRoleOption(role);
    setSelectedRole(normalizedRole);
    if (normalizedRole) {
      setSelectedActingUser(null);
    }
  };

  const handleCaseSelect = (event, caseItem) => {
    setSelectedCase(caseItem);
    setSelectedRole(null);
    setSelectedProvider(null);
    setLawFirm(false);
    setSelectedLawFirm(null);
    setToggleAddProvider(false);
    setProviderSearch("");
    setLawFirmSearch("");

    if (selectedActingUser) {
      setSelectedActingUser(null);
    }
  };

  const handleLawFirmToggle = () => {
    setLawFirm((prev) => {
      if (prev) {
        setSelectedLawFirm(null);
        setLawFirmSearch("");
      }
      return !prev;
    });
  };

  const handleLawFirmChange = (lawFirmIdValue) => {
    if (!lawFirmIdValue) {
      setSelectedLawFirm(null);
      return;
    }
    const lawFirmOption = lawFirmList.find((firm) => firm.id === lawFirmIdValue);
    setSelectedLawFirm(normalizeLawFirmOption(lawFirmOption));
  };

  const handleProviderChange = (providerIdValue) => {
    if (!providerIdValue) {
      setSelectedProvider(null);
      return;
    }
    const provider = providerList.find((prov) => prov.id === providerIdValue);
    setSelectedProvider(provider ?? null);
  };

  const handleActionDetailFocus = () => {
    if (draftDetail) {
      setActionDetail(draftDetail);
    }
  };

  const goToActionables = () => {
    if (linkDisabled) {
      return;
    }
    navigate(`/client-detail/${currentCaseId}/actionable-items`);
  };

  const assignableUserOptions = useMemo(() => {
    if (!selectedActingUser || typeof selectedActingUser !== "object") {
      return assignableUserList;
    }

    const exists = assignableUserList.some((user) => user?.id === selectedActingUser?.id);
    return exists ? assignableUserList : [selectedActingUser, ...assignableUserList];
  }, [assignableUserList, selectedActingUser]);

  const roleOptions = useMemo(() => {
    if (!selectedRole || typeof selectedRole !== "object") {
      return roleList;
    }

    const exists = roleList.some((role) => role?.id === selectedRole?.id);
    return exists ? roleList : [selectedRole, ...roleList];
  }, [roleList, selectedRole]);

  const casesFieldProps = {
    clientCases,
    selectedCase,
    handleCaseSelect,
    setCaseSearch,
    tooltipTitle,
    goToActionables,
    linkDisabled,
  };

  const assignmentSectionProps = {
    roleOptions,
    selectedRole,
    handleRoleSelect,
    selectedActingUser,
    assignableUserOptions,
    handleActingUserSelect,
    setUserSearch,
    setRoleSearch,
  };

  const lawFirmSectionProps = {
    lawFirm,
    handleLawFirmToggle,
    lawFirmList,
    selectedLawFirm,
    handleLawFirmChange,
    setLawFirmSearch,
  };

  const providerSectionProps = {
    toggleAddProvider,
    selectedCase,
    handleProvCheck,
    selectedProvider,
    providerList,
    handleProviderChange,
    setProviderSearch,
  };

  const dueDateFieldProps = {
    dateTimeVal,
    defaultDate,
    setDateTimeValue,
  };

  const actionDetailFieldProps = {
    actionDetail,
    setActionDetail,
    handleActionDetailFocus,
  };

  return {
    defaultDate,
    selectedCase,
    clientCases,
    handleCaseSelect,
    setCaseSearch,
    tooltipTitle,
    goToActionables,
    linkDisabled,
    selectedRole,
    roleOptions,
    handleRoleSelect,
    selectedActingUser,
    assignableUserOptions,
    handleActingUserSelect,
    setUserSearch,
    lawFirm,
    handleLawFirmToggle,
    lawFirmList,
    selectedLawFirm,
    handleLawFirmChange,
    toggleAddProvider,
    handleProvCheck,
    selectedProvider,
    providerList,
    handleProviderChange,
    dateTimeVal,
    setDateTimeValue,
    actionDetail,
    setActionDetail,
    handleActionDetailFocus,
    handleNewActionSubmit,
    patchAction,
    handleClose,
    openError,
    setOpenError,
    errorArray,
    isLoadingActionItem,
    casesFieldProps,
    assignmentSectionProps,
    lawFirmSectionProps,
    providerSectionProps,
    dueDateFieldProps,
    actionDetailFieldProps,
  };
};

export default useAddActionItemModal;
